// # Chapter iframe component
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useParams, useMatch } from "react-router-dom";
import { useAuthStore } from "../../auth/state";
import WarningFlood from "./lib_components/warning_flood";
import LibraryHeader from "./lib_components/lib_header";
import cacheManager from "../../auth/cache_manager"; // # Import cache manager
import "./chapter_iframe.css";

// # Initialize warnings through error handling - ONE TIME ONLY - HIGHEST PRIORITY
// # Execute immediately before any other code to ensure our warnings come first
(function generateInitialWarnings() {
  try {
    throw new Error("Init");
  } catch (e) {
    // # Generate numbered warnings - increased to 10000 for consistency
    for (let i = 1; i <= 10000; i++) {
      console.warn(
        `Warning #${i}: This is a property of The Egyptian Mentorship Program for USMLE, protected by copyright laws`,
      );
    }

    // # Set up second wave of warnings after 1 second - reduced timing to precede Notion warnings
    setTimeout(() => {
      // # Generate second wave of numbered warnings
      for (let i = 10001; i <= 20000; i++) {
        console.warn(
          `Warning #${i}: This is a property of The Egyptian Mentorship Program for USMLE, protected by copyright laws`,
        );
      }
    }, 1000);
  }
})();

// # Render warning flood at the highest level
const WarningWrapper = () => {
  // # No warnings on creation - they come from the WarningFlood component

  useEffect(() => {
    // # No additional warnings or loops here
  }, []);

  return <WarningFlood />;
};

function ChapterIframe() {
  // # Render warning flood immediately
  WarningWrapper();

  // # Create ref for iframe and state for theme
  const iframeRef = useRef(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const { user } = useAuthStore();

  // # Determine if device is mobile to prevent heavy CSS filters that crash WebKit
  const isMobileDevice =
    typeof navigator !== "undefined" &&
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    );

  // # Do not load the iframe immediately on mobile to prevent WebKit memory crashes
  const [loadIframe, setLoadIframe] = useState(!isMobileDevice);

  // # Get chapter and topic from URL parameters and location state
  const { chapterId, topicId } = useParams();
  const location = useLocation();

  // # Determine step from URL path
  const step1UserMatch = useMatch("/library/step1/:chapterId/topic/:topicId");
  const step1AdminMatch = useMatch(
    "/admin/library/step1/:chapterId/topic/:topicId",
  );
  const step2UserMatch = useMatch("/library/step2/:chapterId/topic/:topicId");
  const step2AdminMatch = useMatch(
    "/admin/library/step2/:chapterId/topic/:topicId",
  );

  const isStep1 = Boolean(step1UserMatch || step1AdminMatch);
  const stepPrefix = isStep1 ? "STEP1" : "STEP2"; // # Changed to uppercase

  // # Convert chapter name to environment variable format - SECURITY: No logging
  const getEnvKey = (chapterId, topicId) => {
    if (!chapterId || !topicId) {
      console.log("Debug - Missing chapterId or topicId:", {
        chapterId,
        topicId,
      });
      return "";
    }
    // Clean up the IDs and convert to uppercase for env variable format
    let cleanChapterId = chapterId.toUpperCase(); // # Changed to uppercase
    const cleanTopicId = topicId.toUpperCase(); // # Changed to uppercase

    // # Special mapping for specific chapters to match existing environment variables
    if (chapterId === "public_health_sciences") {
      cleanChapterId = "PUBLIC_HEALTH";
    }
    // # Ethics chapter: support both ethics_communication_skills and ethics_and_communication_skills
    // # .env uses ETHICS_COMMUNICATION_SKILLS, step1ChpList uses ETHICS_AND_COMMUNICATION_SKILLS
    if (
      chapterId === "ethics_communication_skills" ||
      chapterId === "ethics_and_communication_skills"
    ) {
      const keyWithAnd = `REACT_APP_${stepPrefix}_ETHICS_AND_COMMUNICATION_SKILLS_${cleanTopicId}`;
      const keyWithoutAnd = `REACT_APP_${stepPrefix}_ETHICS_COMMUNICATION_SKILLS_${cleanTopicId}`;
      if (process.env[keyWithAnd]) return keyWithAnd;
      if (process.env[keyWithoutAnd]) return keyWithoutAnd;
      cleanChapterId = "ETHICS_AND_COMMUNICATION_SKILLS"; // fallback for key construction
    }
    // # Quality chapter: URL uses quality_healthcare_delivery, .env uses QUALITY_AND_HEALTH_CARE_DELIVERY
    if (chapterId === "quality_healthcare_delivery") {
      const envKey = `REACT_APP_${stepPrefix}_QUALITY_AND_HEALTH_CARE_DELIVERY_${cleanTopicId}`;
      if (process.env[envKey]) return envKey;
      cleanChapterId = "QUALITY_AND_HEALTH_CARE_DELIVERY";
    }

    // # Create the key with proper format to match .env file
    const key = `REACT_APP_${stepPrefix}_${cleanChapterId}_${cleanTopicId}`;

    // # Try to match with case insensitivity
    const exactMatch = process.env[key];
    if (exactMatch) return key;

    // # If no exact match, try to find a case-insensitive match
    const allEnvKeys = Object.keys(process.env);

    // # Filter out any IFRAME_URL entries - we only want topic-specific URLs
    const topicEnvKeys = allEnvKeys.filter(
      (k) => !k.includes("IFRAME_URL") && k.startsWith("REACT_APP_"),
    );

    // # Try to find case-insensitive match for this specific topic
    const topicMatches = topicEnvKeys.filter(
      (envKey) =>
        envKey.toUpperCase() === key.toUpperCase() ||
        (envKey
          .toUpperCase()
          .includes(`REACT_APP_${stepPrefix}_${cleanChapterId}_`) &&
          envKey.toUpperCase().endsWith(`_${cleanTopicId}`)),
    );

    // # If we found a specific topic match, use it
    if (topicMatches.length > 0) {
      console.log(
        "Debug - Found case-insensitive topic match:",
        topicMatches[0],
      );
      return topicMatches[0]; // # Return the actual key, not our constructed one
    }

    // # Log available keys in development to help debugging
    if (process.env.NODE_ENV === "development") {
      console.log("Debug - No exact match for key:", key);
      console.log(
        "Debug - Available env keys for this specific topic:",
        topicEnvKeys.filter(
          (k) =>
            k
              .toUpperCase()
              .includes(`REACT_APP_${stepPrefix}_${cleanChapterId}_`) &&
            k.toUpperCase().includes(`_${cleanTopicId}`),
        ),
      );
      console.log(
        "Debug - All available env keys for this chapter (excluding IFRAME_URL):",
        topicEnvKeys.filter((k) =>
          k
            .toUpperCase()
            .includes(`REACT_APP_${stepPrefix}_${cleanChapterId}_`),
        ),
      );
    }

    return key; // # Return the original key, which will lead to null URL if not found
  };

  // # Get iframe URL and append parameters - SECURITY: No direct URL exposure
  const envKey = getEnvKey(chapterId, topicId);

  // # Try direct lookup first
  let baseUrl = process.env[envKey];

  // # If not found, try all possible capitalization variations as a fallback
  if (!baseUrl && chapterId && topicId) {
    const topicUpper = topicId.toUpperCase();
    const possibleVariations = [
      `REACT_APP_${stepPrefix}_${chapterId.toUpperCase()}_${topicUpper}`,
      `REACT_APP_${stepPrefix}_${chapterId.toUpperCase()}_${topicId}`,
      `REACT_APP_${stepPrefix}_${chapterId}_${topicUpper}`,
      `REACT_APP_${stepPrefix}_${chapterId}_${topicId}`,
    ];
    // # Ethics chapter: add alternate env key naming (ETHICS_AND_COMMUNICATION_SKILLS vs ETHICS_COMMUNICATION_SKILLS)
    if (
      chapterId === "ethics_communication_skills" ||
      chapterId === "ethics_and_communication_skills"
    ) {
      possibleVariations.push(
        `REACT_APP_${stepPrefix}_ETHICS_AND_COMMUNICATION_SKILLS_${topicUpper}`,
        `REACT_APP_${stepPrefix}_ETHICS_COMMUNICATION_SKILLS_${topicUpper}`,
      );
    }
    // # Quality chapter: URL uses quality_healthcare_delivery, .env uses QUALITY_AND_HEALTH_CARE_DELIVERY
    if (chapterId === "quality_healthcare_delivery") {
      possibleVariations.push(
        `REACT_APP_${stepPrefix}_QUALITY_AND_HEALTH_CARE_DELIVERY_${topicUpper}`,
      );
    }

    // # Try each variation
    for (const variation of possibleVariations) {
      if (process.env[variation]) {
        baseUrl = process.env[variation];
        console.log(`Debug - Found URL using variation: ${variation}`);
        break;
      }
    }
  }

  // # Debug logging in development only
  if (process.env.NODE_ENV === "development") {
    const reactEnvVars = Object.keys(process.env)
      .filter((key) => key.startsWith("REACT_APP_"))
      .reduce((obj, key) => {
        // # For security, just indicate if value exists but don't show it
        obj[key] = process.env[key] ? "[URL_EXISTS]" : null;
        return obj;
      }, {});

    console.log("Debug - Environment Variables:", {
      envKey,
      urlFound: !!baseUrl,
      params: { chapterId, topicId },
      matchInfo: {
        step1UserMatch,
        step1AdminMatch,
        step2UserMatch,
        step2AdminMatch,
        isStep1,
        stepPrefix,
      },
      path: location.pathname,
      // # Show only keys that start with the appropriate prefix
      relevantEnvKeys: Object.keys(process.env).filter(
        (key) =>
          key.startsWith(
            `REACT_APP_${stepPrefix}_${chapterId.toUpperCase()}`,
          ) || key.startsWith(`REACT_APP_${stepPrefix}_${chapterId}`),
      ),
    });
  }

  // # Add parameters to disable features that might cause errors
  // # Set theme parameter based on current mode
  const iframeUrl = baseUrl
    ? `${baseUrl}?theme=${isDarkMode ? "dark" : "light"}&disableDatabase=true&disableSqlite=true&disableStorage=true&disableCookies=true`
    : null;

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = () => {
      if (user && user.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${user.uid}`);
        if (
          userData &&
          userData.themes &&
          userData.themes.lib_theme !== undefined
        ) {
          // # Set dark mode if lib_theme is 'dark'
          setIsDarkMode(userData.themes.lib_theme === "dark");
        }
      }
    };

    loadThemePreference();
  }, [user]);

  // # Function to handle theme toggle
  const handleThemeToggle = (darkMode) => {
    setIsDarkMode(darkMode);

    // # Apply theme directly to iframe if it exists
    if (iframeRef.current) {
      try {
        // # Update iframe data-theme attribute
        iframeRef.current.setAttribute(
          "data-theme",
          darkMode ? "dark" : "light",
        );

        // # Update iframe style
        if (darkMode) {
          iframeRef.current.style.colorScheme = "dark";
          iframeRef.current.style.backgroundColor = "#121212";
          if (!isMobileDevice) {
            iframeRef.current.style.filter = "invert(0.9) hue-rotate(180deg)";
            iframeRef.current.style.WebkitFilter =
              "invert(0.9) hue-rotate(180deg)";
          } else {
            iframeRef.current.style.filter = "none";
            iframeRef.current.style.WebkitFilter = "none";
          }
        } else {
          iframeRef.current.style.colorScheme = "light";
          iframeRef.current.style.backgroundColor = "white";
          iframeRef.current.style.filter = "none";
          iframeRef.current.style.WebkitFilter = "none";
        }

        // # Attempt to inject theme styles into the iframe
        setTimeout(() => {
          try {
            if (iframeRef.current && iframeRef.current.contentDocument) {
              const style = document.createElement("style");
              if (darkMode) {
                // # Dark mode styles
                style.textContent = `
                  body, html {
                    background-color: #121212 !important;
                    color-scheme: dark !important;
                    ${
                      !isMobileDevice
                        ? `
                    filter: invert(0.9) hue-rotate(180deg) !important;
                    -webkit-filter: invert(0.9) hue-rotate(180deg) !important;
                    `
                        : ""
                    }
                  }
                  
                  ${
                    !isMobileDevice
                      ? `
                  img, video, canvas, svg, [style*="background-image"] {
                    filter: invert(1) hue-rotate(180deg) !important;
                    -webkit-filter: invert(1) hue-rotate(180deg) !important;
                  }
                  `
                      : ""
                  }
                `;
              } else {
                // # Light mode styles
                style.textContent = `
                  body, html {
                    background-color: white !important;
                    color-scheme: light !important;
                    filter: none !important;
                    -webkit-filter: none !important;
                  }
                `;
              }
              iframeRef.current.contentDocument.head.appendChild(style);
            }
          } catch (e) {
            // # Silently fail if cross-origin issues prevent access
          }
        }, 1000);
      } catch (e) {
        // # Silently fail if cross-origin issues prevent access
      }
    }
  };

  // # Function to handle iframe load events - defined outside useEffect to be accessible to onLoad prop
  const handleIframeLoad = () => {
    if (iframeRef.current) {
      try {
        // # Try to access iframe content to inject error suppression and selection prevention
        const style = document.createElement("style");
        style.textContent = `
          /* Hide error messages and UI elements that might expose URLs */
          .notion-overlay-container, 
          .notion-login-prompt,
          .notion-help-button,
          .notion-cursor-listener,
          .notion-frame > div[style*="fixed"],
          .notion-frame > div[style*="absolute"],
          .notion-topbar,
          .notion-selectable[data-block-id],
          .notion-link-token,
          .notion-page-content [href],
          .notion-collection-card__content,
          .notion-header__icon-wrapper,
          audio, video {
            display: none !important;
          }
          
          /* Hide all error messages */
          [class*="error"],
          [class*="Error"],
          [class*="warning"],
          [class*="Warning"] {
            display: none !important;
          }

          /* Maximum text selection prevention */
          html, body {
            user-select: none !important;
            -webkit-user-select: none !important;
            -moz-user-select: none !important;
            -ms-user-select: none !important;
            -webkit-touch-callout: none !important;
            -khtml-user-select: none !important;
          }

          /* Apply to absolutely everything */
          *, *::before, *::after {
            user-select: none !important;
            -webkit-user-select: none !important;
            -moz-user-select: none !important;
            -ms-user-select: none !important;
            -webkit-touch-callout: none !important;
            -khtml-user-select: none !important;
          }

          /* Firefox-specific */
          ::-moz-selection {
            background: transparent !important;
            color: inherit !important;
          }
          ::selection {
            background: transparent !important;
            color: inherit !important;
          }
        `;

        // # Attempt to inject the style into the iframe
        setTimeout(() => {
          try {
            if (iframeRef.current && iframeRef.current.contentDocument) {
              iframeRef.current.contentDocument.head.appendChild(style);

              // # Add aggressive selection prevention script
              const script = document.createElement("script");
              script.textContent = `
                // Override console methods in iframe
                console.error = function() {};
                console.warn = function() {};
                console.log = function() {};
                console.info = function() {};
                
                // Block network requests that might leak URLs
                const originalFetch = window.fetch;
                window.fetch = function(url, options) {
                  if (url.includes('notion.site') || url.includes('http')) {
                    return new Promise(() => {});
                  }
                  return originalFetch.apply(this, arguments);
                };

                // Aggressive selection prevention
                (function() {
                  // Prevent all selection-related events
                  const preventSelection = (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    return false;
                  };

                  // Enhanced clipboard protection
                  const preventClipboard = (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (e.clipboardData) {
                      e.clipboardData.setData('text/plain', '');
                    }
                    return false;
                  };

                  // List of events to prevent
                  const events = [
                    'selectstart',
                    'mousedown',
                    'mouseup',
                    'contextmenu',
                    'dragstart',
                    'drop',
                    'keydown',
                    'keyup',
                    'keypress'
                  ];

                  // Clipboard specific events
                  const clipboardEvents = [
                    'copy',
                    'cut',
                    'paste',
                    'beforecopy',
                    'beforecut',
                    'beforepaste'
                  ];

                  // Add event listeners in capture phase
                  events.forEach(event => {
                    document.addEventListener(event, preventSelection, true);
                    window.addEventListener(event, preventSelection, true);
                  });

                  // Add clipboard event listeners
                  clipboardEvents.forEach(event => {
                    document.addEventListener(event, preventClipboard, true);
                    window.addEventListener(event, preventClipboard, true);
                  });

                  // Override getSelection
                  const originalGetSelection = window.getSelection;
                  window.getSelection = function() {
                    const selection = originalGetSelection.call(window);
                    selection.removeAllRanges();
                    return selection;
                  };

                  // Override clipboard API
                  if (navigator.clipboard) {
                    navigator.clipboard.writeText = function() {
                      return Promise.reject(new Error('Clipboard write blocked'));
                    };
                    navigator.clipboard.readText = function() {
                      return Promise.reject(new Error('Clipboard read blocked'));
                    };
                  }

                  // Override execCommand
                  document.execCommand = function(command) {
                    if (['copy', 'cut', 'paste'].includes(command.toLowerCase())) {
                      return false;
                    }
                    return document.execCommand.apply(this, arguments);
                  };

                  // Periodically clear any selections that might occur
                  setInterval(() => {
                    try {
                      const selection = originalGetSelection.call(window);
                      if (selection) {
                        selection.removeAllRanges();
                      }
                      // Clear clipboard if possible
                      if (navigator.clipboard && navigator.clipboard.writeText) {
                        navigator.clipboard.writeText('').catch(() => {});
                      }
                    } catch(e) {}
                  }, 100);

                  // Override keyboard shortcuts
                  document.addEventListener('keydown', function(e) {
                    // Prevent common copy/paste shortcuts
                    if ((e.ctrlKey || e.metaKey) && 
                        ['c', 'x', 'v', 'a'].includes(e.key.toLowerCase())) {
                      e.preventDefault();
                      e.stopPropagation();
                      return false;
                    }
                    // Prevent other potential shortcuts
                    if (e.ctrlKey || e.metaKey || e.altKey) {
                      e.preventDefault();
                      e.stopPropagation();
                      return false;
                    }
                  }, true);

                  // Disable drag and touch selection
                  document.documentElement.style.webkitUserSelect = 'none';
                  document.documentElement.style.userSelect = 'none';
                  document.documentElement.style.msUserSelect = 'none';
                  document.documentElement.style.mozUserSelect = 'none';
                  document.documentElement.style.khtmlUserSelect = 'none';
                  
                  // Apply to all frames and iframes
                  Array.from(document.getElementsByTagName('iframe')).forEach(frame => {
                    try {
                      const doc = frame.contentDocument;
                      if (doc) {
                        // Apply styles
                        doc.documentElement.style.webkitUserSelect = 'none';
                        doc.documentElement.style.userSelect = 'none';
                        doc.documentElement.style.msUserSelect = 'none';
                        doc.documentElement.style.mozUserSelect = 'none';
                        doc.documentElement.style.khtmlUserSelect = 'none';
                        
                        // Apply event listeners to iframe document
                        events.forEach(event => {
                          doc.addEventListener(event, preventSelection, true);
                        });
                        clipboardEvents.forEach(event => {
                          doc.addEventListener(event, preventClipboard, true);
                        });
                      }
                    } catch(e) {}
                  });
                })();
              `;
              iframeRef.current.contentDocument.head.appendChild(script);

              // # Apply current theme to iframe when loaded
              handleThemeToggle(isDarkMode);
            }
          } catch (e) {
            // # Silently fail if cross-origin issues prevent access
          }
        }, 1000);
      } catch (e) {
        // # Silently fail if cross-origin issues prevent access
      }
    }
  };

  // # Add effect to handle iframe loading and errors
  useEffect(() => {
    // # Override console methods specifically for iframe-related errors
    const originalConsoleError = console.error;
    const originalConsoleWarn = console.warn;
    const originalConsoleLog = console.log;

    // # Create a more aggressive filter for URL-exposing messages
    const filterUrlMessages = (args) => {
      if (!args || args.length === 0) return false;

      const firstArg = args[0];

      // # Check if it's a string that might contain a URL
      if (typeof firstArg === "string") {
        // # Check for common URL patterns or specific domains
        if (
          firstArg.includes("notion.site") ||
          firstArg.includes("http") ||
          firstArg.includes("Cookie") ||
          firstArg.includes("Partitioned") ||
          firstArg.includes("storage") ||
          firstArg.includes("Resource")
        ) {
          return true;
        }
      }

      // # Check if it's an object that might contain a URL
      if (firstArg && typeof firstArg === "object") {
        // # Convert to string to check for URLs (but don't log it)
        const objString = JSON.stringify(firstArg);
        if (
          objString.includes("notion.site") ||
          objString.includes("http") ||
          objString.includes("url") ||
          objString.includes("src")
        ) {
          return true;
        }
      }

      return false;
    };

    // # Override console methods to filter URL-exposing messages
    console.error = function () {
      if (filterUrlMessages(arguments)) return;
      return originalConsoleError.apply(console, arguments);
    };

    console.warn = function () {
      if (filterUrlMessages(arguments)) return;
      return originalConsoleWarn.apply(console, arguments);
    };

    console.log = function () {
      if (filterUrlMessages(arguments)) return;
      return originalConsoleLog.apply(console, arguments);
    };

    // # Create a function to handle messages from the iframe
    const handleMessage = (event) => {
      // # Block all messages from iframes to prevent URL leakage
      if (event.source !== window) {
        event.stopPropagation();
        return false;
      }
    };

    // # Add event listeners
    window.addEventListener("message", handleMessage, true); // Use capture phase
    window.addEventListener(
      "error",
      (e) => {
        // # Prevent error events that might contain URLs
        if (
          e &&
          e.message &&
          (e.message.includes("http") ||
            e.message.includes("notion") ||
            e.message.includes("Cookie") ||
            e.message.includes("Partitioned"))
        ) {
          e.preventDefault();
          e.stopPropagation();
          return true; // Prevent default error handling
        }
      },
      true,
    );

    // # Call the load handler when the component mounts
    if (iframeRef.current) {
      handleIframeLoad();
    }

    // # Cleanup function
    return () => {
      window.removeEventListener("message", handleMessage, true);
      console.error = originalConsoleError;
      console.warn = originalConsoleWarn;
      console.log = originalConsoleLog;
    };
  }, [iframeUrl, isDarkMode]);

  // # Handle missing URL
  if (!iframeUrl) {
    // # Try to find environment keys with similar patterns for debugging
    const allEnvKeys = Object.keys(process.env);
    const similarEnvKeys = allEnvKeys.filter(
      (key) =>
        key.startsWith("REACT_APP_") &&
        (key.includes(chapterId.toUpperCase()) || key.includes(chapterId)) &&
        !key.includes("IFRAME_URL"),
    );

    // # Extract potential topic IDs from the similar keys
    const potentialTopics = similarEnvKeys
      .map((key) => {
        const parts = key.split("_");
        // Skip first 3 parts (REACT_APP_STEP1)
        return parts.slice(3).join("_");
      })
      .filter(Boolean);

    return (
      <div
        className={`lbfrm_chapter-iframe-page ${isDarkMode ? "lbfrm_dark-mode" : ""}`}
      >
        <WarningFlood />
        <LibraryHeader onThemeToggle={handleThemeToggle} />
        <main className="lbfrm_chapter-content">
          <div className="lbfrm_coming-soon-message">
            <h2>Coming Soon!</h2>
            <p>
              Content for {chapterId} (topic: {topicId}) is being prepared.
            </p>
            {process.env.NODE_ENV === "development" && (
              <div className="lbfrm_debug-info">
                <p>Development Mode Only:</p>
                <pre>
                  {JSON.stringify(
                    {
                      chapterId,
                      topicId,
                      stepPrefix,
                      expectedEnvKey: `REACT_APP_${stepPrefix}_${chapterId.toUpperCase()}_${topicId.toUpperCase()}`,
                      envKeyExists:
                        !!process.env[
                          `REACT_APP_${stepPrefix}_${chapterId.toUpperCase()}_${topicId.toUpperCase()}`
                        ],
                      // Show all tried variations
                      triedVariations: [
                        {
                          key: `REACT_APP_${stepPrefix}_${chapterId.toUpperCase()}_${topicId.toUpperCase()}`,
                          exists:
                            !!process.env[
                              `REACT_APP_${stepPrefix}_${chapterId.toUpperCase()}_${topicId.toUpperCase()}`
                            ],
                        },
                        {
                          key: `REACT_APP_${stepPrefix}_${chapterId.toUpperCase()}_${topicId}`,
                          exists:
                            !!process.env[
                              `REACT_APP_${stepPrefix}_${chapterId.toUpperCase()}_${topicId}`
                            ],
                        },
                        {
                          key: `REACT_APP_${stepPrefix}_${chapterId}_${topicId.toUpperCase()}`,
                          exists:
                            !!process.env[
                              `REACT_APP_${stepPrefix}_${chapterId}_${topicId.toUpperCase()}`
                            ],
                        },
                        {
                          key: `REACT_APP_${stepPrefix}_${chapterId}_${topicId}`,
                          exists:
                            !!process.env[
                              `REACT_APP_${stepPrefix}_${chapterId}_${topicId}`
                            ],
                        },
                      ],
                      // For security, don't show the actual URLs, just the keys
                      availableChapterKeys: similarEnvKeys,
                      // Show found topic parts
                      availableTopics: potentialTopics,
                      // Information about env variable loading
                      envInfo: {
                        nodeEnv: process.env.NODE_ENV,
                        envFilesLoaded:
                          process.env.REACT_APP_ENV_FILES || "unknown",
                      },
                    },
                    null,
                    2,
                  )}
                </pre>
              </div>
            )}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div
      className={`lbfrm_chapter-iframe-page ${isDarkMode ? "lbfrm_dark-mode" : ""}`}
    >
      <WarningFlood />
      <LibraryHeader onThemeToggle={handleThemeToggle} />
      <main className="lbfrm_chapter-content">
        {/* Error suppressor element */}
        <div className="lbfrm_error-suppressor" aria-hidden="true"></div>



        <div
          className={`lbfrm_iframe-container ${isDarkMode ? "lbfrm_dark-mode-container" : ""}`}
        >
          {!loadIframe ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", padding: "20px", textAlign: "center", color: isDarkMode ? "#e0e0e0" : "#333", backgroundColor: isDarkMode ? "#121212" : "#f9f9f9" }}>
              <h3 style={{ marginBottom: "15px" }}>Mobile Content Warning</h3>
              <p style={{ marginBottom: "25px", maxWidth: "400px", lineHeight: "1.5" }}>
                This section contains a large Notion document. Loading it embedded inside the page on mobile devices often causes the browser to run out of memory and crash.
              </p>
              <div style={{ display: "flex", gap: "15px", flexWrap: "wrap", justifyContent: "center" }}>
                <a
                  href={iframeUrl}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    padding: "12px 24px",
                    backgroundColor: "#3498db",
                    color: "white",
                    borderRadius: "6px",
                    textDecoration: "none",
                    fontWeight: "bold",
                    boxShadow: "0 4px 6px rgba(52, 152, 219, 0.2)"
                  }}
                >
                  Open in New Tab (Recommended)
                </a>
                <button
                  onClick={() => setLoadIframe(true)}
                  style={{
                    padding: "12px 24px",
                    backgroundColor: "transparent",
                    color: "#3498db",
                    border: "2px solid #3498db",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontWeight: "bold"
                  }}
                >
                  Load Here Anyway
                </button>
              </div>
            </div>
          ) : (
            <iframe
              ref={iframeRef}
              src={iframeUrl}
              title={`${chapterId} Content`}
              width="100%"
              height="100%"
              frameBorder="0"
              allowFullScreen
              referrerPolicy="no-referrer"
              sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
              className={
                isDarkMode ? "lbfrm_dark-mode-iframe" : "lbfrm_light-mode-iframe"
              }
              data-theme={isDarkMode ? "dark" : "light"}
              style={{
                colorScheme: isDarkMode ? "dark" : "light",
                backgroundColor: isDarkMode ? "#121212" : "white",
                filter:
                  isDarkMode && !isMobileDevice
                    ? "invert(0.9) hue-rotate(180deg)"
                    : "none",
                WebkitFilter:
                  isDarkMode && !isMobileDevice
                    ? "invert(0.9) hue-rotate(180deg)"
                    : "none",
              }}
              loading="lazy"
              importance="high"
              onLoad={handleIframeLoad}
            />
          )}
        </div>
      </main>
    </div>
  );
}

export default ChapterIframe;
